/**
 * Thin client for the local admin endpoints (/api/admin/*).
 *
 * Only used by the in-app admin panel (build-gated behind VITE_ADMIN). The bearer
 * token is entered once and kept in localStorage — it is NOT baked into the build.
 */
const TOKEN_KEY = 'grid_admin_token';

function getToken(): string {
    let t = localStorage.getItem(TOKEN_KEY);
    if (!t) {
        t = window.prompt('Admin token (ADMIN_TOKEN on the local server):') || '';
        if (t) localStorage.setItem(TOKEN_KEY, t);
    }
    return t;
}

export function clearAdminToken(): void {
    localStorage.removeItem(TOKEN_KEY);
}

async function req(path: string, opts: RequestInit = {}): Promise<any> {
    const res = await fetch(`/api/admin${path}`, {
        ...opts,
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getToken()}`,
            ...(opts.headers || {}),
        },
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
        if (res.status === 401) clearAdminToken(); // bad token → re-prompt next time
        throw new Error(body.error || `HTTP ${res.status}`);
    }
    return body;
}

export interface CandidateDraft { player: string; score: number | null; }
export interface CandidateGrid {
    id: number; grid_hash: string; grid_data: string; size: number; is_vortex: boolean; drafts: CandidateDraft[];
    /** The 12-game set this grid was authored to play; null ⇒ not stated, read as the classic 12. */
    game_scope: string | null;
}
export interface RefScore {
    player: string; total: number | null; unsolved: number | null;
    /** Improved since the last push — the remote serves a worse par until the next sync. */
    unpushed?: boolean;
}
/** A challenge's own state. `synced_at` non-null means published → frozen. */
export interface ChallengeState {
    id: number; title: string; starts_at: string; ends_at: string; game_scope: string;
    synced_at: string | null; remote_id: number | null;
}
/** A candidate that has been scheduled: same actions, plus challenge state and par. */
export interface ScheduledGrid extends CandidateGrid {
    challenge: ChallengeState;
    refs: RefScore[];
}
export interface Slot { starts_at: string; ends_at: string }
/** Free slots per publishing track — Base opens Mondays, Vortex Wednesdays. */
export interface TrackSlots { base: Slot[]; vortex: Slot[] }
export interface JobState {
    id: string; kind: string; status: 'running' | 'done' | 'failed'; lines: string[]; exitCode?: number;
}

/** Where a grid sits in the authoring pipeline (see docs/challenge-pipeline.md). */
export type Lifecycle = 'local' | 'candidate' | 'scheduled' | 'published';

export const lifecycleOf = (g: CandidateGrid | ScheduledGrid): Lifecycle => {
    const c = (g as ScheduledGrid).challenge;
    if (!c) return 'candidate';
    return c.synced_at ? 'published' : 'scheduled';
};

export interface CandidatesResponse {
    grids: CandidateGrid[];
    all_scope: string;
    defaults: CandidateGrid[];
    scheduled: ScheduledGrid[];
    slots: TrackSlots;
}

export const AdminApi = {
    candidates: (): Promise<CandidatesResponse> => req('/grids/candidates'),
    generate: (count: number, sizes?: number[]): Promise<{ grids: CandidateGrid[] }> =>
        req('/grids/generate', { method: 'POST', body: JSON.stringify({ count, sizes }) }),
    /** Adopt a grid the curator generated in-app into the backlog. Idempotent. */
    /** `game_scope` is the grid's authored 12-game set; omit for the classic 12. */
    addCandidate: (grid_data: string, game_scope?: string): Promise<{ id: number; grid_hash: string; size: number; added: boolean; game_scope: string | null }> =>
        req('/grids', { method: 'POST', body: JSON.stringify({ grid_data, game_scope }) }),
    solve: (id: number, timeLimitSec?: number): Promise<{ job_id: string }> =>
        req(`/grids/${id}/solve`, { method: 'POST', body: JSON.stringify({ timeLimitSec }) }),
    saveDraft: (id: number, solution_data: string, player = 'Gyrodiot'): Promise<{ player: string; solved: number; attached?: string }> =>
        req(`/grids/${id}/draft`, { method: 'POST', body: JSON.stringify({ player, solution_data }) }),
    schedule: (id: number, scope?: string): Promise<{ challengeId: number; title: string; starts_at: string; ends_at: string; attached: string[] }> =>
        req(`/grids/${id}/schedule`, { method: 'POST', body: JSON.stringify({ scope }) }),
    reschedule: (challengeId: number, starts_at: string): Promise<{ title: string; starts_at: string; ends_at: string }> =>
        req(`/challenges/${challengeId}/reschedule`, { method: 'POST', body: JSON.stringify({ starts_at }) }),
    unschedule: (challengeId: number): Promise<{ unscheduled: number; gridId: number }> =>
        req(`/challenges/${challengeId}`, { method: 'DELETE' }),
    deleteGrid: (id: number): Promise<{ deleted: number }> => req(`/grids/${id}`, { method: 'DELETE' }),
    job: (jobId: string): Promise<JobState> => req(`/jobs/${jobId}`),
    sync: (): Promise<SyncReport> => req('/sync', { method: 'POST' }),
    verify: (): Promise<VerifyReport> => req('/sync/verify', { method: 'POST' }),
    /** Pull player leaderboards from the remote — all published challenges, or one by local id. */
    remoteScores: (localChallengeId?: number): Promise<RemoteScoresReport> =>
        req(`/remote/leaderboards${localChallengeId !== undefined ? `?challenge=${localChallengeId}` : ''}`),
};

export interface SyncReport {
    ok: boolean;
    error?: string;
    remote?: string;
    challenges: Array<{ title: string; remoteId?: number; solutions?: string[]; error?: string }>;
}

export interface VerifyReport {
    ok: boolean;
    error?: string;
    remote?: string;
    lines: string[];
    corrected: number;
}

export interface RemoteLeaderboardEntry {
    rank: number;
    user_id: string;
    username: string | null;
    total_score: number;
    unsolved_count: number;
    score_breakdown: string;
    submitted_at: string;
}
export interface RemoteChallengeScores {
    local_id: number;
    remote_id: number;
    title: string;
    starts_at: string;
    is_vortex: boolean;
    entries: RemoteLeaderboardEntry[];
    error?: string;
}
export interface RemoteScoresReport {
    ok: boolean;
    error?: string;
    remote?: string;
    challenges: RemoteChallengeScores[];
}
